/**
 * AttendanceHeatmap — GitHub-contributions-style year view of one employee's
 * attendance. 53 weeks × 7 days = ~371 cells per year, each color-coded by
 * machine status with hover tooltips showing detailed punch info.
 *
 * Color legend:
 *   green     P (present)
 *   amber     HD / late ≥30m / early-out ≥30m (flagged but not absent)
 *   red       A (absent without approved leave)
 *   blue      H (machine holiday) / WO (weekly off)
 *   gray      no data (pre-sync or post-cutoff)
 *   ring      OT > 0 → small emerald dot in cell corner
 *
 * Designed to feel calm, scannable, and information-dense without clutter.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle, Info, Flame, Award } from 'lucide-react';
import { formatDateToLocal } from '../../lib/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}`,
});

// ─── Color logic ──────────────────────────────────────────────────────────
const CELL_BASE = 'transition-all duration-150 cursor-pointer hover:ring-2 hover:ring-pink-300 hover:ring-offset-1';
const colorFor = (cell) => {
  if (!cell) return { fill: '#f3f4f6', label: 'no data' }; // gray-100
  const s = (cell.status || '').toUpperCase();
  if (s === 'P') {
    if ((cell.late_minutes || 0) >= 30 || (cell.early_out_minutes || 0) >= 30) {
      return { fill: '#fbbf24', label: 'present (late/early)' }; // amber-400
    }
    return { fill: '#10b981', label: 'present' }; // emerald-500
  }
  if (s === 'HD') return { fill: '#f59e0b', label: 'half-day' }; // amber-500
  if (s === 'A') return { fill: '#ef4444', label: 'absent' }; // red-500
  if (s === 'H') return { fill: '#3b82f6', label: 'holiday' }; // blue-500
  if (s === 'WO') return { fill: '#d1d5db', label: 'weekly off' }; // gray-300
  return { fill: '#e5e7eb', label: s || 'unknown' };
};

const fmtMin = (m) => {
  if (!m) return '';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
};

// ─── Grid math ────────────────────────────────────────────────────────────
// Build 53 columns × 7 rows for a given year. Each cell = one day.
const buildYearGrid = (year) => {
  const start = new Date(year, 0, 1);
  // Start from the Sunday on or before Jan 1
  const dow = start.getDay();
  const gridStart = new Date(start);
  gridStart.setDate(start.getDate() - dow);
  const cells = [];
  const months = []; // [{ name, col }]
  let lastMonth = -1;
  for (let col = 0; col < 53; col++) {
    for (let row = 0; row < 7; row++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + col * 7 + row);
      const inYear = d.getFullYear() === year;
      cells.push({
        date: formatDateToLocal(d),
        row,
        col,
        inYear,
        month: d.getMonth(),
        day: d.getDate(),
      });
    }
    // Month label = the month of the first row's date in this column, if it changed
    const headDate = new Date(gridStart);
    headDate.setDate(gridStart.getDate() + col * 7);
    if (headDate.getFullYear() === year && headDate.getMonth() !== lastMonth) {
      months.push({ name: headDate.toLocaleString('en-US', { month: 'short' }), col });
      lastMonth = headDate.getMonth();
    }
  }
  return { cells, months };
};

// ─── Main component ───────────────────────────────────────────────────────
export const AttendanceHeatmap = ({ employeeId, employeeName, initialYear }) => {
  const today = new Date();
  const [year, setYear] = useState(initialYear ?? today.getFullYear());

  // If the outer filter bar's From-date year changes, sync our year picker
  // so the heatmap reflects the same temporal context as siblings.
  useEffect(() => {
    if (initialYear && initialYear !== year) setYear(initialYear);
  }, [initialYear, year]);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(null); // { x, y, cell, day }

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch full year. Limit 500 — well under any single employee's 371 days.
      const r = await axios.get(`${API}/api/admin/attendance/daily`, {
        headers: auth(),
        params: {
          from_date: `${year}-01-01`,
          to_date: `${year}-12-31`,
          employee_id: employeeId,
          limit: 500,
        },
      });
      const byDate = {};
      for (const item of r.data.items || []) byDate[item.date] = item;
      setData(byDate);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [employeeId, year]);

  useEffect(() => { load(); }, [load]);

  const { cells, months } = useMemo(() => buildYearGrid(year), [year]);

  // Aggregate stats for the side panel
  const stats = useMemo(() => {
    const rows = Object.values(data);
    const present = rows.filter((r) => (r.status || '').toUpperCase() === 'P').length;
    const absent = rows.filter((r) => (r.status || '').toUpperCase() === 'A').length;
    const late = rows.filter((r) => (r.late_minutes || 0) > 0).length;
    const early = rows.filter((r) => (r.early_out_minutes || 0) > 0).length;
    const ot = rows.reduce((s, r) => s + (r.overtime_minutes || 0), 0);
    const work = rows.reduce((s, r) => s + (r.work_minutes || 0), 0);
    // Streak (consecutive present days, walking backward from today)
    let streak = 0;
    const cursor = new Date();
    while (cursor.getFullYear() === year || streak === 0) {
      const key = formatDateToLocal(cursor);
      const cell = data[key];
      const s = (cell?.status || '').toUpperCase();
      if (s === 'P' || s === 'HD') streak += 1;
      else if (s === 'WO' || s === 'H') { /* skip weekends/holidays */ }
      else break;
      cursor.setDate(cursor.getDate() - 1);
      if (cursor.getFullYear() < year) break;
    }
    return { present, absent, late, early, ot, work, streak };
  }, [data, year]);

  const SIZE = 12;
  const GAP = 3;
  const LABEL_HEIGHT = 18;
  const LABEL_WIDTH = 28;
  const width = LABEL_WIDTH + 53 * (SIZE + GAP);
  const height = LABEL_HEIGHT + 7 * (SIZE + GAP);

  return (
    <div className="space-y-4" data-testid="attendance-heatmap">
      {/* Header: year selector + summary chips */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setYear(year - 1)}
            data-testid="heatmap-prev-year"
            className="p-1.5 border border-neutral-200 rounded-md hover:bg-neutral-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h3 className="text-base font-semibold text-[#1a1a2e] min-w-[180px]">
            {year} · {employeeName || 'Pick an employee'}
          </h3>
          <button
            onClick={() => setYear(year + 1)}
            disabled={year >= today.getFullYear()}
            data-testid="heatmap-next-year"
            className="p-1.5 border border-neutral-200 rounded-md hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Chip label="Present" value={stats.present} accent="emerald" />
          <Chip label="Absent" value={stats.absent} accent="red" />
          <Chip label="Late" value={stats.late} accent="amber" />
          <Chip label="Early-out" value={stats.early} accent="amber" />
          <Chip label="OT" value={fmtMin(stats.ot)} accent="blue" />
          {stats.streak > 1 && (
            <Chip label="Streak" value={`${stats.streak} days`} accent="pink" icon={Flame} />
          )}
        </div>
      </div>

      {/* Heatmap SVG */}
      <div className="bg-white border border-neutral-200 rounded-lg p-4 relative overflow-x-auto">
        {!employeeId ? (
          <div className="py-12 text-center text-sm text-neutral-500" data-testid="heatmap-empty">
            <Info className="w-5 h-5 mx-auto mb-2 text-neutral-300" />
            Pick an employee from the Snapshot tab to view their year-long heatmap.
          </div>
        ) : loading ? (
          <div className="py-12 text-center text-sm text-neutral-500 inline-flex items-center justify-center w-full gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : error ? (
          <div className="py-3 text-xs text-red-600 inline-flex items-center gap-2">
            <AlertCircle className="w-3.5 h-3.5" /> {error}
          </div>
        ) : (
          <>
            <svg width={width} height={height} className="block">
              {/* Month labels along top */}
              {months.map((m) => (
                <text
                  key={m.col}
                  x={LABEL_WIDTH + m.col * (SIZE + GAP)}
                  y={12}
                  className="text-[10px] fill-neutral-500"
                >
                  {m.name}
                </text>
              ))}
              {/* Day-of-week labels (every other row to keep clean) */}
              {['', 'Mon', '', 'Wed', '', 'Fri', ''].map((lbl, i) => (
                <text
                  key={i}
                  x={0}
                  y={LABEL_HEIGHT + i * (SIZE + GAP) + SIZE - 2}
                  className="text-[9px] fill-neutral-400"
                >
                  {lbl}
                </text>
              ))}
              {/* Cells */}
              {cells.map((c) => {
                if (!c.inYear) return null;
                const cell = data[c.date];
                const { fill } = colorFor(cell);
                const hasOT = (cell?.overtime_minutes || 0) > 0;
                return (
                  <g key={c.date}>
                    <rect
                      x={LABEL_WIDTH + c.col * (SIZE + GAP)}
                      y={LABEL_HEIGHT + c.row * (SIZE + GAP)}
                      width={SIZE}
                      height={SIZE}
                      rx={2.5}
                      fill={fill}
                      className={CELL_BASE}
                      data-testid={`heatmap-cell-${c.date}`}
                      onMouseEnter={(e) => {
                        const rect = e.target.getBoundingClientRect();
                        setHover({ x: rect.left + rect.width / 2, y: rect.top, cell, day: c });
                      }}
                      onMouseLeave={() => setHover(null)}
                    />
                    {hasOT && (
                      <circle
                        cx={LABEL_WIDTH + c.col * (SIZE + GAP) + SIZE - 2.5}
                        cy={LABEL_HEIGHT + c.row * (SIZE + GAP) + 2.5}
                        r={1.5}
                        fill="#059669"
                      />
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Tooltip */}
            {hover && (
              <div
                className="fixed z-50 pointer-events-none bg-[#1a1a2e] text-white text-[11px] rounded-lg px-3 py-2 shadow-xl"
                style={{
                  left: hover.x,
                  top: hover.y - 8,
                  transform: 'translate(-50%, -100%)',
                  minWidth: 180,
                }}
              >
                <div className="font-semibold">{new Date(hover.day.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                {hover.cell ? (
                  <div className="mt-1 space-y-0.5 text-neutral-300">
                    <div>Status: <span className="text-white font-medium">{(hover.cell.status || '—').toUpperCase()}</span></div>
                    {hover.cell.in_time && <div>In: <span className="text-white">{hover.cell.in_time}</span> · Out: <span className="text-white">{hover.cell.out_time || '—'}</span></div>}
                    {hover.cell.work_minutes > 0 && <div>Work: <span className="text-white">{fmtMin(hover.cell.work_minutes)}</span></div>}
                    {hover.cell.late_minutes > 0 && <div className="text-amber-300">Late: {fmtMin(hover.cell.late_minutes)}</div>}
                    {hover.cell.early_out_minutes > 0 && <div className="text-amber-300">Early out: {fmtMin(hover.cell.early_out_minutes)}</div>}
                    {hover.cell.overtime_minutes > 0 && <div className="text-emerald-300">OT: {fmtMin(hover.cell.overtime_minutes)}</div>}
                  </div>
                ) : (
                  <div className="mt-1 text-neutral-400 italic">No machine data</div>
                )}
              </div>
            )}
          </>
        )}

        {/* Legend */}
        {employeeId && !loading && (
          <div className="flex items-center gap-3 mt-4 text-[11px] text-neutral-500 flex-wrap">
            <span>Legend:</span>
            <LegendChip color="#10b981" label="Present" />
            <LegendChip color="#fbbf24" label="Present + late/early" />
            <LegendChip color="#f59e0b" label="Half-day" />
            <LegendChip color="#ef4444" label="Absent" />
            <LegendChip color="#3b82f6" label="Holiday" />
            <LegendChip color="#d1d5db" label="Weekly off" />
            <LegendChip color="#f3f4f6" label="No data" />
            <span className="inline-flex items-center gap-1">
              <span className="relative inline-block w-3 h-3 bg-neutral-300 rounded-sm">
                <span className="absolute top-0 right-0 w-1.5 h-1.5 bg-emerald-600 rounded-full" />
              </span>
              <span>OT recorded</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────────
const accents = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  pink: 'bg-pink-50 text-pink-700 border-pink-200',
};
const Chip = ({ label, value, accent = 'emerald', icon: Icon }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-full border ${accents[accent]}`}>
    {Icon && <Icon className="w-3 h-3" />}
    <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
    <span className="font-semibold tabular-nums">{value}</span>
  </span>
);

const LegendChip = ({ color, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
    <span>{label}</span>
  </span>
);

export default AttendanceHeatmap;
